import { useRef, useState, type ReactElement } from 'react';

/**
 * Enter sends, Shift+Enter breaks the line. Grows to eight rows, then
 * scrolls. Disabled while a stream runs, and it says why rather than just
 * greying out.
 */
export function Composer({
  disabled,
  onSend,
}: {
  disabled: boolean;
  onSend: (message: string) => void;
}): ReactElement {
  const [value, setValue] = useState('');
  const areaRef = useRef<HTMLTextAreaElement | null>(null);

  const submit = (): void => {
    if (disabled || value.trim() === '') return;
    onSend(value);
    setValue('');
    const area = areaRef.current;
    if (area !== null) area.style.height = 'auto';
  };

  return (
    <form
      className="composer"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <div className="composer__inner">
        <div className="composer__row">
          <textarea
            ref={areaRef}
            className="field composer__input"
            rows={1}
            value={value}
            placeholder={disabled ? 'Streaming. Stop the response to write.' : 'Ask something'}
            aria-label="Message"
            disabled={disabled}
            onChange={(event) => {
              setValue(event.target.value);
              const area = event.currentTarget;
              area.style.height = 'auto';
              const lineHeight = 24;
              area.style.height = `${String(Math.min(area.scrollHeight, lineHeight * 8 + 16))}px`;
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                submit();
              }
            }}
          />
          <button
            type="submit"
            className="button button--primary"
            disabled={disabled || value.trim() === ''}
          >
            Send
          </button>
        </div>
        {/* Always rendered, empty while disabled, rather than removed: the
            hint used to disappear mid-stream and the row above it jumped up
            to fill the gap. Reserving the line keeps the composer's height
            steady through the whole turn. */}
        <p className="composer__hint">
          {!disabled && (
            <>
              <kbd>Enter</kbd> sends, <kbd>Shift</kbd> plus <kbd>Enter</kbd> starts a new line.
            </>
          )}
        </p>
      </div>
    </form>
  );
}
